import { useState, useEffect, useCallback } from 'react';
import {
  institutionRoles,
  type Role,
  type Permission,
  type CreateRoleInput,
  type UpdateRoleInput,
} from '@/services/institution';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import styles from './RolesPage.module.css';

export function RolesPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState<Partial<CreateRoleInput>>({});
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteRole, setDeleteRole] = useState<Role | null>(null);

  // View role details
  const [viewRole, setViewRole] = useState<Role | null>(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await institutionRoles.list();
      if (response.success && response.data) {
        setRoles(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await institutionRoles.getPermissions();
      if (response.success && response.data) {
        setPermissions(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [fetchRoles, fetchPermissions]);

  const filteredRoles = roles.filter((role) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      role.name.toLowerCase().includes(search) ||
      role.code.toLowerCase().includes(search) ||
      role.description?.toLowerCase().includes(search)
    );
  });

  function openCreateModal() {
    setEditingRole(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      permissions: [],
    });
    setSelectedPermissions(new Set());
    setError(null);
    setShowModal(true);
  }

  function openEditModal(role: Role) {
    setEditingRole(role);
    setFormData({
      code: role.code,
      name: role.name,
      description: role.description || '',
      permissions: role.permissions,
    });
    setSelectedPermissions(new Set(role.permissions));
    setError(null);
    setShowModal(true);
  }

  function togglePermission(code: string) {
    const newSet = new Set(selectedPermissions);
    if (newSet.has(code)) {
      newSet.delete(code);
    } else {
      newSet.add(code);
    }
    setSelectedPermissions(newSet);
  }

  function toggleCategory(category: string) {
    const categoryPermissions = permissions.filter(p => p.category === category);
    const allSelected = categoryPermissions.every(p => selectedPermissions.has(p.code));

    const newSet = new Set(selectedPermissions);
    categoryPermissions.forEach(p => {
      if (allSelected) {
        newSet.delete(p.code);
      } else {
        newSet.add(p.code);
      }
    });
    setSelectedPermissions(newSet);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const input = {
        code: formData.code!.toUpperCase(),
        name: formData.name!,
        description: formData.description || undefined,
        permissions: Array.from(selectedPermissions),
      };

      let response;
      if (editingRole) {
        response = await institutionRoles.update(editingRole.id, input as UpdateRoleInput);
      } else {
        response = await institutionRoles.create(input as CreateRoleInput);
      }

      if (response.success) {
        setShowModal(false);
        fetchRoles();
      } else {
        setError(response.error?.message || 'Failed to save role');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!deleteRole) return;

    setSubmitting(true);
    try {
      const response = await institutionRoles.delete(deleteRole.id);
      if (response.success) {
        setDeleteRole(null);
        fetchRoles();
      } else {
        setError(response.error?.message || 'Failed to delete role');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  // Group permissions by category
  const permissionsByCategory = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Roles</h1>
          <p className={styles.subtitle}>Manage user roles and permissions</p>
        </div>
        <Button onClick={openCreateModal}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Create Role
        </Button>
      </div>

      <div className={styles.searchBar}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          type="text"
          placeholder="Search roles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.rolesGrid}>
        {loading ? (
          <div className={styles.loading}>Loading roles...</div>
        ) : filteredRoles.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z" />
            </svg>
            <p>No roles found</p>
            {searchQuery && (
              <Button variant="secondary" onClick={() => setSearchQuery('')}>
                Clear Search
              </Button>
            )}
          </div>
        ) : (
          filteredRoles.map((role) => (
            <div key={role.id} className={styles.roleCard}>
              <div className={styles.roleHeader}>
                <div className={styles.roleInfo}>
                  <div className={styles.roleIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <div>
                    <h3 className={styles.roleName}>{role.name}</h3>
                    <span className={styles.roleCode}>{role.code}</span>
                  </div>
                </div>
                {role.isSystemRole && (
                  <span className={styles.systemBadge}>System</span>
                )}
              </div>

              {role.description && (
                <p className={styles.roleDescription}>{role.description}</p>
              )}

              <div className={styles.roleStats}>
                <div className={styles.roleStat}>
                  <span className={styles.statValue}>{role.permissions.length}</span>
                  <span className={styles.statLabel}>Permissions</span>
                </div>
                <div className={styles.roleStat}>
                  <span className={styles.statValue}>{formatDate(role.createdAt)}</span>
                  <span className={styles.statLabel}>Created</span>
                </div>
              </div>

              <div className={styles.roleActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewRole(role)}
                >
                  View
                </Button>
                {!role.isSystemRole && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(role)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteRole(role)}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingRole ? 'Edit Role' : 'Create Role'}
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <Input
            label="Role Code"
            value={formData.code || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
            required
            disabled={!!editingRole}
            hint="Unique identifier (e.g., RECORDS_CLERK)"
          />

          <Input
            label="Role Name"
            value={formData.name || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
            hint="Display name for the role"
          />

          <Input
            label="Description"
            value={formData.description || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          />

          <div className={styles.permissionsSection}>
            <h4 className={styles.permissionsTitle}>
              Permissions ({selectedPermissions.size} selected)
            </h4>
            <div className={styles.permissionsGrid}>
              {Object.entries(permissionsByCategory).map(([category, perms]) => {
                const allSelected = perms.every(p => selectedPermissions.has(p.code));
                const someSelected = perms.some(p => selectedPermissions.has(p.code));

                return (
                  <div key={category} className={styles.permissionCategory}>
                    <div className={styles.categoryHeader}>
                      <label className={styles.categoryCheckbox}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => {
                            if (el) el.indeterminate = someSelected && !allSelected;
                          }}
                          onChange={() => toggleCategory(category)}
                        />
                        <span className={styles.categoryName}>{category}</span>
                      </label>
                    </div>
                    <div className={styles.permissionsList}>
                      {perms.map((perm) => (
                        <label key={perm.code} className={styles.permissionItem}>
                          <input
                            type="checkbox"
                            checked={selectedPermissions.has(perm.code)}
                            onChange={() => togglePermission(perm.code)}
                          />
                          <span className={styles.permissionName}>{perm.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Role Modal */}
      <Modal
        isOpen={!!viewRole}
        onClose={() => setViewRole(null)}
        title={viewRole?.name || 'Role Details'}
      >
        {viewRole && (
          <div className={styles.viewRole}>
            <div className={styles.viewRoleHeader}>
              <span className={styles.viewRoleCode}>{viewRole.code}</span>
              {viewRole.isSystemRole && <span className={styles.systemBadge}>System Role</span>}
            </div>

            {viewRole.description && (
              <p className={styles.viewRoleDescription}>{viewRole.description}</p>
            )}

            <div className={styles.viewRolePermissions}>
              <h4>Permissions ({viewRole.permissions.length})</h4>
              <div className={styles.permissionTags}>
                {viewRole.permissions.length === 0 ? (
                  <span className={styles.noPermissions}>No permissions assigned</span>
                ) : (
                  viewRole.permissions.map((perm) => (
                    <span key={perm} className={styles.permissionTag}>{perm}</span>
                  ))
                )}
              </div>
            </div>

            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setViewRole(null)}>
                Close
              </Button>
              {!viewRole.isSystemRole && (
                <Button onClick={() => { setViewRole(null); openEditModal(viewRole); }}>
                  Edit Role
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteRole}
        onClose={() => setDeleteRole(null)}
        title="Delete Role"
      >
        <div className={styles.deleteConfirm}>
          <p>Are you sure you want to delete the role <strong>{deleteRole?.name}</strong>?</p>
          <p className={styles.deleteWarning}>
            This action cannot be undone. Users with this role will lose the associated permissions.
          </p>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setDeleteRole(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={submitting}>
              {submitting ? 'Deleting...' : 'Delete Role'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
